import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ServingCommunitySection from '../../components/feature/ServingCommunitySection';
import ProductNav from '../../components/feature/ProductNav';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

const faqs = [
  {
    q: 'What does final expense insurance cover?',
    a: 'Final expense insurance covers funeral and burial costs, outstanding medical bills, credit card debt, and other end-of-life expenses. The death benefit is typically $5,000–$25,000 and is paid directly to your beneficiary, tax-free.',
  },
  {
    q: 'Do I need a medical exam to qualify?',
    a: 'No. Final expense insurance uses simplified underwriting — just a few health questions. Most applicants ages 50–85 are approved without any medical exam, blood draw, or doctor visit required.',
  },
  {
    q: 'How much does final expense insurance cost?',
    a: 'Premiums typically range from $30–$100/month depending on your age, gender, health, and the coverage amount you choose. A 65-year-old woman in good health can often get $15,000 of coverage for around $40–$55/month.',
  },
  {
    q: 'Is final expense insurance the same as burial insurance?',
    a: "Yes — final expense, burial insurance, and funeral insurance all refer to the same type of permanent whole life policy designed to cover end-of-life costs. The policy doesn't expire as long as premiums are paid.",
  },
  {
    q: 'Can I get final expense coverage if I have health issues?',
    a: "Absolutely. Final expense insurance is specifically designed for seniors who may not qualify for traditional coverage. Even if you have diabetes, COPD, or heart disease, there are guaranteed issue policies that cannot deny you based on health.",
  },
  {
    q: 'How quickly are death benefits paid?',
    a: "Most carriers pay claims within 24–72 hours of receiving a completed claim form and death certificate. This is specifically why final expense insurance is so important — funeral homes typically require payment upfront.",
  },
];

export default function FinalExpensePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showBooking, setShowBooking] = useState(false);

  useEffect(() => {
    document.title = 'Final Expense Insurance Indianapolis | Hoosier Life';

    const metaTags: { name?: string; property?: string; content: string }[] = [
      { name: 'description', content: 'No-exam burial insurance for Indiana seniors. Coverage from $30/mo. Guaranteed acceptance ages 50-85. Free quote. Call (317) 648-2983.' },
      { name: 'keywords', content: 'final expense insurance Indianapolis, burial insurance Indiana, funeral insurance Indiana seniors, no exam life insurance Indiana' },
      { property: 'og:title', content: 'Final Expense Insurance Indianapolis | Hoosier Life' },
      { property: 'og:description', content: 'No-exam burial insurance for Indiana seniors. Coverage from $30/mo. Guaranteed acceptance ages 50-85. Free quote.' },
      { property: 'og:url', content: `${SITE_URL}/final-expense` },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/c3e1920856dceb40be4fb4267b83814e.jpg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Final Expense Insurance Indianapolis | Hoosier Life' },
      { name: 'twitter:description', content: 'No-exam burial insurance for Indiana seniors. Coverage from $30/mo. Guaranteed acceptance ages 50-85.' },
      { name: 'twitter:image', content: 'https://readdy.ai/api/search-image?query=warm%20compassionate%20elderly%20couple%20reviewing%20life%20insurance%20documents%20at%20kitchen%20table%20soft%20natural%20light%20peaceful%20Indiana%20home%20comfortable%20living%20room%20financial%20security%20burial%20planning&width=1200&height=630&seq=fe-og-image-v2&orientation=landscape' },
    ];

    const created: HTMLElement[] = [];

    metaTags.forEach((tag) => {
      const key = tag.name || tag.property || '';
      const attr = tag.name ? 'name' : 'property';
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', tag.content);
    });

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
      created.push(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/final-expense`);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': `${SITE_URL}/final-expense#service`,
        name: 'Final Expense Insurance Indianapolis',
        description: 'Affordable final expense and burial insurance for Indiana seniors. No medical exam required. Coverage from $5,000–$25,000.',
        provider: { '@id': `${SITE_URL}/#organization` },
        areaServed: { '@type': 'State', name: 'Indiana' },
        serviceType: 'Final Expense Insurance',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '94',
          bestRating: '5',
          worstRating: '1',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${SITE_URL}/final-expense#webpage`,
        url: `${SITE_URL}/final-expense`,
        name: 'Final Expense Insurance Indianapolis | No-Exam Burial Insurance for Indiana Seniors',
        description: 'No-exam burial insurance for Indiana seniors. Coverage from $30/mo. Guaranteed acceptance ages 50-85. Free quote. Call (317) 648-2983.',
        inLanguage: 'en-US',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        dateModified: new Date().toISOString(),
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2:first-of-type', '.key-takeaways-heading']
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Final Expense Insurance', item: `${SITE_URL}/final-expense` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ];

    // InsuranceProduct schema — injected separately with named ID for safe cleanup
    const ipSchema = {
      '@context': 'https://schema.org',
      '@type': 'InsuranceProduct',
      '@id': `${SITE_URL}/final-expense#insurance-product`,
      name: 'Final Expense Insurance',
      description: 'Whole life final expense insurance covering funeral, burial, and end-of-life costs for Indiana seniors. No medical exam required. Guaranteed issue available. Coverage from $5,000 to $50,000 with fixed premiums that never increase.',
      url: `${SITE_URL}/final-expense`,
      category: 'Life Insurance',
      brand: { '@type': 'Brand', name: 'Hoosier Life Insurance' },
      provider: { '@type': 'Organization', '@id': `${SITE_URL}/#organization` },
      isRelatedTo: { '@type': 'Organization', '@id': `${SITE_URL}/#business` },
      areaServed: { '@type': 'State', name: 'Indiana' },
      offers: {
        '@type': 'Offer',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          priceCurrency: 'USD',
          description: 'Starting from $20/month for Indiana seniors',
        },
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/final-expense`,
      },
    };
    const ipScriptFE = document.createElement('script');
    ipScriptFE.id = 'schema-final-expense-insurance-product';
    ipScriptFE.type = 'application/ld+json';
    ipScriptFE.textContent = JSON.stringify(ipSchema);
    document.head.appendChild(ipScriptFE);

    const scripts: HTMLScriptElement[] = [];
    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.id = `schema-final-expense-${i}`;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      scripts.push(script);
    });

    return () => {
      created.forEach((el) => { try { document.head.removeChild(el); } catch (e) { /* ignore */ } });
      scripts.forEach((s) => { try { document.head.removeChild(s); } catch (e) { /* ignore */ } });
      const ipEl = document.getElementById('schema-final-expense-insurance-product');
      if (ipEl) ipEl.remove();
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <ProductNav />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative pt-28 pb-20 bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] overflow-hidden">
          {/* Decorative glow orbs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-[#D97642]/8 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 right-1/3 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-8">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span>/</span>
              <span className="text-white">Final Expense Insurance</span>
            </nav>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left column */}
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#D97642]/20 rounded-full mb-6">
                  <i className="ri-heart-pulse-line text-[#D97642]"></i>
                  <span className="text-sm font-medium text-[#F4A97A]">No Medical Exam Required</span>
                </span>
                <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-5">
                  Final Expense Insurance for Indiana Seniors
                </h1>
                <p className="text-xl text-white/80 mb-8 leading-relaxed">
                  Don&apos;t leave your family with a $10,000+ funeral bill to figure out during the worst week of their lives. Final expense insurance covers those costs — no doctor visit, no health exam, and approval in as little as 24 hours. Starting at just $30/month.
                </p>
                {/* Trust badges */}
                <div className="flex flex-wrap gap-3 mb-8">
                  {[
                    { icon: 'ri-checkbox-circle-line', label: 'No Medical Exam' },
                    { icon: 'ri-user-age-line', label: 'Ages 50-85' },
                    { icon: 'ri-time-line', label: 'Coverage in 24hrs' },
                    { icon: 'ri-money-dollar-circle-line', label: 'From $30/mo' },
                  ].map((badge) => (
                    <div key={badge.label} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm text-white/90">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${badge.icon} text-[#D97642] text-sm`}></i>
                      </div>
                      <span>{badge.label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/instant-quote"
                    className="px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#c26535] transition-colors text-center whitespace-nowrap"
                  >
                    Get Free Quote
                  </Link>
                  <Link
                    to="/book-appointment"
                    className="px-8 py-4 bg-white/10 text-white font-bold rounded-lg border border-white/30 hover:bg-white/20 transition-colors text-center whitespace-nowrap"
                  >
                    Book Free Consultation
                  </Link>
                </div>
              </div>
              {/* Right column — visible hero image card */}
              <div className="hidden lg:block">
                <div className="relative w-full h-80 rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=warm%20compassionate%20elderly%20Indiana%20couple%20in%20their%2070s%20smiling%20peacefully%20at%20home%20reviewing%20life%20insurance%20documents%20at%20kitchen%20table%20soft%20natural%20light%20comfortable%20living%20room%20final%20expense%20planning%20financial%20security&width=600&height=380&seq=final-expense-hero-twocol-2025&orientation=landscape"
                    alt="Final Expense Insurance for Indiana Seniors"
                    title="Final Expense Insurance — Affordable Burial Coverage for Indiana Seniors Ages 50-85"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    width={600}
                    height={380}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A3A52]/60 via-transparent to-transparent"></div>
                </div>
                {/* White info card overlay */}
                <div className="bg-white rounded-xl p-5 mt-4 border border-gray-100">
                  <p className="text-xs font-semibold text-[#D97642] uppercase tracking-wider mb-3">Coverage Summary</p>
                  <div className="space-y-2">
                    {[
                      { label: 'Coverage Amount', value: '$5,000–$25,000' },
                      { label: 'Monthly Premium', value: 'From $30/mo' },
                      { label: 'Age Range', value: '50–85 years old' },
                      { label: 'Approval Time', value: '24–48 hours' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
                        <span className="text-sm text-gray-600">{item.label}</span>
                        <span className="text-sm font-bold text-[#1A3A52]">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Takeaways — AI SEO Signal */}
        <section className="py-8 bg-[#1A3A52]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
              </div>
              <div className="flex-1">
                <h2 className="text-white font-bold text-lg mb-3">Key Takeaways: Final Expense Insurance in Indiana</h2>
                <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                  {[
                    'The average Indiana funeral costs $8,000–$12,000 — this policy covers it completely so your family doesn\'t have to',
                    'No doctor visit, no blood draw, no medical exam — just a few simple health questions and you\'re done',
                    'Guaranteed issue plans exist for any health condition — you truly cannot be turned down, no matter what',
                    'Premiums start as low as $30/month and NEVER go up — locked in for life the day you sign up',
                    'Indiana insurers pay death claims within 24–72 hours — funeral homes expect payment fast, this delivers',
                    'It\'s a permanent whole life policy — it never expires and builds a small cash value over time too',
                  ].map((bullet, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/90 text-sm">
                      <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 text-white/50 text-xs">Last reviewed March 2025 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Final Expense Insurance */}
        <section className="py-16 bg-[#F9F7F4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Why Indiana Families Choose Final Expense Coverage</h2>
              <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
                The average funeral in Indiana costs $8,000–$12,000. Funeral homes want that money up front — usually within days. Final expense insurance means your family never has to scramble, borrow, or argue over money when they&apos;re already grieving.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: 'Guaranteed Acceptance',
                  desc: 'Many plans accept all applicants ages 50–85 regardless of health history. No medical exam, no blood draw, no doctor visit.',
                },
                {
                  icon: 'ri-money-dollar-circle-line',
                  title: 'Affordable Premiums',
                  desc: 'Plans start at just $30/month. Your premium never increases and your coverage never decreases once your policy is issued.',
                },
                {
                  icon: 'ri-time-line',
                  title: 'Fast Payout',
                  desc: 'Death benefits are paid within 24–72 hours — funeral homes require payment upfront, so speed matters for your family.',
                },
                {
                  icon: 'ri-heart-line',
                  title: 'Lifetime Coverage',
                  desc: "Final expense is a whole life policy — it never expires as long as premiums are paid, unlike term insurance that ends after a set number of years.",
                },
                {
                  icon: 'ri-group-line',
                  title: 'Protects Your Family',
                  desc: 'Your loved ones are already grieving — final expense insurance ensures they can focus on healing rather than scrambling for funds.',
                },
                {
                  icon: 'ri-file-list-3-line',
                  title: 'Flexible Benefit Use',
                  desc: 'The tax-free death benefit can cover funeral costs, medical bills, credit card debt, or any expense your family chooses.',
                },
              ].map((item) => (
                <div key={item.title} className="bg-white rounded-xl p-6 border border-gray-100">
                  <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-2xl text-[#1A3A52]`} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Coverage & Cost Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#1A3A52] mb-6">Sample Monthly Rates for Indiana Seniors</h2>
                <p className="text-[#4A5568] mb-8 leading-relaxed">
                  Rates are based on age and health. The table below shows approximate monthly premiums for a $15,000 final expense policy for Indiana residents.
                </p>
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#1A3A52] text-white">
                        <th className="px-4 py-3 text-left font-semibold">Age</th>
                        <th className="px-4 py-3 text-left font-semibold">Male (Non-Smoker)</th>
                        <th className="px-4 py-3 text-left font-semibold">Female (Non-Smoker)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { age: '55', male: '$42/mo', female: '$34/mo' },
                        { age: '60', male: '$54/mo', female: '$44/mo' },
                        { age: '65', male: '$68/mo', female: '$55/mo' },
                        { age: '70', male: '$88/mo', female: '$72/mo' },
                        { age: '75', male: '$118/mo', female: '$96/mo' },
                        { age: '80', male: '$158/mo', female: '$128/mo' },
                      ].map((row, i) => (
                        <tr key={row.age} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                          <td className="px-4 py-3 font-medium text-[#1A3A52]">{row.age}</td>
                          <td className="px-4 py-3 text-[#4A5568]">{row.male}</td>
                          <td className="px-4 py-3 text-[#4A5568]">{row.female}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-[#4A5568] mt-3">
                  * Rates are estimates for illustration only. Your actual premium depends on carrier, health, and coverage amount. Smoker rates are typically 30–40% higher.
                </p>
              </div>
              <div className="space-y-6">
                <div className="bg-[#F9F7F4] rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#1A3A52] mb-4">What the Average Indiana Funeral Costs</h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Full Traditional Funeral', cost: '$9,000–$15,000' },
                      { label: 'Graveside Service & Burial', cost: '$6,000–$10,000' },
                      { label: 'Cremation with Memorial', cost: '$3,000–$6,000' },
                      { label: 'Direct Cremation', cost: '$1,500–$3,000' },
                      { label: 'Outstanding Medical Bills', cost: '$2,000–$25,000+' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center justify-between border-b border-gray-200 pb-3">
                        <span className="text-[#4A5568] text-sm">{item.label}</span>
                        <span className="font-bold text-[#1A3A52] text-sm">{item.cost}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-[#D97642]/10 rounded-xl p-6 border border-[#D97642]/20">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-[#D97642] text-xl mt-0.5" />
                    <div>
                      <p className="font-bold text-[#1A3A52] mb-1">Don't leave your family with the bill</p>
                      <p className="text-[#4A5568] text-sm leading-relaxed">
                        Without coverage, your family must pay funeral costs out of pocket within days — often while still in shock. A simple $15,000 policy can eliminate all of that stress for as little as $40–$70/month.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Plan Types */}
        <section className="py-16 bg-[#F9F7F4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Types of Final Expense Plans</h2>
              <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
                We shop multiple carriers to find the right plan for your health situation and budget.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Level Benefit',
                  badge: 'Best Value',
                  badgeColor: 'bg-green-500',
                  desc: 'Full death benefit from day one. Best rates, requires answering a few health questions. Ideal for applicants in generally good health.',
                  features: ['Full coverage from day 1', 'Lowest premiums', 'Health questions required', 'Best for healthy seniors'],
                },
                {
                  title: 'Graded Benefit',
                  badge: 'For Health Issues',
                  badgeColor: 'bg-[#D97642]',
                  desc: 'Partial benefit in years 1–2, full benefit from year 3 on. For applicants with more serious health history.',
                  features: ['Partial benefit years 1–2', 'Full benefit year 3+', 'Moderate health issues OK', 'Moderate premiums'],
                },
                {
                  title: 'Guaranteed Issue',
                  badge: 'No Exam, No Questions',
                  badgeColor: 'bg-[#1A3A52]',
                  desc: 'Cannot be denied for any health reason. Waiting period of 2 years before full benefit. Highest premiums but universal acceptance.',
                  features: ['No health questions', '2-year waiting period', 'Any health condition OK', 'Ages 50–85 accepted'],
                },
              ].map((plan) => (
                <div key={plan.title} className="bg-white rounded-xl p-6 border border-gray-200 flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#1A3A52]">{plan.title}</h3>
                    <span className={`${plan.badgeColor} text-white text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap`}>
                      {plan.badge}
                    </span>
                  </div>
                  <p className="text-[#4A5568] text-sm mb-5 leading-relaxed">{plan.desc}</p>
                  <ul className="space-y-2 mt-auto">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-[#4A5568]">
                        <i className="ri-checkbox-circle-fill text-[#D97642]" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Indiana Families Who Found Peace of Mind</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  name: 'Dorothy H.',
                  city: 'Indianapolis, IN',
                  text: "I'm 71 and was turned down by two other companies because of my heart condition. Benjamin found me a guaranteed issue policy that same day. My family won't have to worry about my funeral costs anymore.",
                  stars: 5,
                },
                {
                  name: 'Robert & Patricia M.',
                  city: 'Carmel, IN',
                  text: "We called just to ask a few questions and ended up with policies for both of us the same afternoon. Simple process, no exam, very affordable. We should have done this years ago.",
                  stars: 5,
                },
                {
                  name: 'Louise T.',
                  city: 'Fishers, IN',
                  text: 'My husband passed unexpectedly and the insurance company paid within 48 hours. I was able to focus on my family instead of figuring out how to pay for the funeral. Worth every penny.',
                  stars: 5,
                },
              ].map((t) => (
                <div key={t.name} className="bg-[#F9F7F4] rounded-xl p-6 border border-gray-100">
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: t.stars }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-[#D97642]" />
                    ))}
                  </div>
                  <p className="text-[#4A5568] text-sm leading-relaxed mb-4">"{t.text}"</p>
                  <div>
                    <p className="font-bold text-[#1A3A52] text-sm">{t.name}</p>
                    <p className="text-[#4A5568] text-xs">{t.city}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-[#F9F7F4]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 cursor-pointer"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-semibold text-[#1A3A52] text-sm leading-snug">{faq.q}</span>
                    <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-[#D97642] text-xl flex-shrink-0`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <p className="text-[#4A5568] text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-16 bg-[#1A3A52]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Protect Your Family Starting Today</h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Get a no-obligation quote in 60 seconds. No medical exam. No pressure. Just peace of mind.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/instant-quote"
                className="px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#c26535] transition-colors whitespace-nowrap"
              >
                Get Free Quote Now
              </Link>
              <a
                href="tel:3176482983"
                className="px-8 py-4 bg-white/10 text-white font-bold rounded-lg border border-white/30 hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2" />
                (317) 648-2983
              </a>
            </div>
          </div>
        </section>

        <StarRatingDisplay rating={4.9} reviewCount={94} productName="Final Expense Insurance Indiana" />
        <ServingCommunitySection productName="Final Expense Insurance" productSlug="final-expense" />

        {/* Related Articles & Internal Links — insert before existing "Explore More Coverage Options" section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D97642]/10 rounded-full mb-4">
                <i className="ri-article-line text-[#D97642]"></i>
                <span className="text-sm font-medium text-[#D97642]">Continue Learning</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Related Articles &amp; Resources</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">Learn more about final expense insurance and senior coverage options from our Indiana specialists.</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Blog: Final Expense Indianapolis */}
              <a href="/blog/final-expense-insurance-indianapolis" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                    <i className="ri-heart-pulse-line text-xl text-[#D97642] group-hover:text-white transition-colors"></i>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-[#D97642] uppercase tracking-wider">Blog</span>
                    <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Final Expense Insurance in Indianapolis: Complete Guide</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">Everything Indiana seniors need to know about burial insurance, costs, and how to qualify.</p>
                  </div>
                </div>
              </a>

              {/* Blog: Burial Insurance Indiana */}
              <a href="/blog/burial-insurance-indiana" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                    <i className="ri-file-heart-line text-xl text-[#D97642] group-hover:text-white transition-colors"></i>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-[#D97642] uppercase tracking-wider">Blog</span>
                    <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Burial Insurance Indiana: What Seniors Need to Know</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">Real Indiana funeral costs, plan types, and which carriers offer the best rates for seniors.</p>
                  </div>
                </div>
              </a>

              {/* Blog: Life Insurance Indiana Seniors */}
              <a href="/blog/life-insurance-indiana-seniors" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                    <i className="ri-user-heart-line text-xl text-[#D97642] group-hover:text-white transition-colors"></i>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-[#D97642] uppercase tracking-wider">Blog</span>
                    <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Life Insurance for Indiana Seniors: Best Options</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">Which life insurance products make the most sense for Indiana seniors in their 60s, 70s, and 80s.</p>
                  </div>
                </div>
              </a>

              {/* Resource: Final Expense Guide for Indiana Seniors */}
              <a href="/resources/final-expense-insurance-indiana-seniors" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0 group-hover:bg-[#1A3A52] transition-colors">
                    <i className="ri-book-open-line text-xl text-[#1A3A52] group-hover:text-white transition-colors"></i>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-[#1A3A52] uppercase tracking-wider">Resource Guide</span>
                    <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Final Expense Insurance for Indiana Seniors: Full Guide</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">Deep-dive resource covering guaranteed issue rules, Medicaid implications, and carrier comparisons.</p>
                  </div>
                </div>
              </a>

              {/* Blog: Seniors Over 60 Indianapolis */}
              <a href="/blog/life-insurance-seniors-over-60-indianapolis" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0 group-hover:bg-[#1A3A52] transition-colors">
                    <i className="ri-group-line text-xl text-[#1A3A52] group-hover:text-white transition-colors"></i>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-[#1A3A52] uppercase tracking-wider">Blog</span>
                    <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Best Life Insurance for Indianapolis Seniors Over 60</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">Honest comparison of final expense, guaranteed issue, and term options for seniors 60–85.</p>
                  </div>
                </div>
              </a>

              {/* Resource: Life Insurance Mistakes */}
              <a href="/resources/life-insurance-mistakes-indiana-families" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0 group-hover:bg-[#1A3A52] transition-colors">
                    <i className="ri-alert-line text-xl text-[#1A3A52] group-hover:text-white transition-colors"></i>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-[#1A3A52] uppercase tracking-wider">Resource Guide</span>
                    <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">7 Life Insurance Mistakes Indiana Families Make</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">Avoid the coverage errors — including waiting too long to buy final expense coverage.</p>
                  </div>
                </div>
              </a>
            </div>

            <div className="mt-10 bg-gradient-to-r from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-3">Ready to Protect Your Family from Burial Costs?</h3>
              <p className="text-white/80 mb-6 max-w-xl mx-auto">No medical exam. No pressure. Our Indiana specialists compare 20+ carriers to find you the best rate in minutes.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/instant-quote"
                  className="px-8 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap"
                >
                  Get My Free Quote
                </Link>
                <a href="tel:+13176482983" className="px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap inline-flex items-center justify-center gap-2">
                  <i className="ri-phone-line"></i>
                  (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-16 bg-[#F9F7F4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Explore More Coverage Options</h2>
              <p className="text-[#4A5568]">Discover other insurance solutions that complement your final expense coverage</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { to: '/term-life', icon: 'ri-calendar-check-line', title: 'Term Life Insurance', desc: 'Affordable protection for a specific period with flexible coverage' },
                { to: '/whole-life', icon: 'ri-shield-check-line', title: 'Whole Life Insurance', desc: 'Lifetime coverage with cash value growth and guaranteed benefits' },
                { to: '/mortgage-protection', icon: 'ri-home-heart-line', title: 'Mortgage Protection', desc: "Protect your family's home if the unexpected happens" },
                { to: '/annuities', icon: 'ri-line-chart-line', title: 'Annuities', desc: 'Secure retirement income and tax-deferred growth strategies' },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] transition-colors">
                    <i className={`${item.icon} text-2xl text-[#1A3A52] group-hover:text-white`} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                  <p className="text-[#4A5568] text-sm mb-4">{item.desc}</p>
                  <div className="flex items-center text-[#D97642] font-semibold text-sm">
                    Learn More <i className="ri-arrow-right-line ml-1" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <MobileProductCTABar productName="Final Expense Insurance" quoteHref="/instant-quote" />

      {showBooking && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center">
            <h3 className="text-2xl font-bold text-[#1A3A52] mb-4">Book a Free Consultation</h3>
            <p className="text-[#4A5568] mb-6">We'll find the best final expense plan for your situation in minutes.</p>
            <div className="flex flex-col gap-3">
              <Link to="/book-appointment" className="px-6 py-3 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-colors whitespace-nowrap">
                Schedule Online
              </Link>
              <a href="tel:3176482983" className="px-6 py-3 bg-[#1A3A52] text-white font-bold rounded-lg hover:bg-[#243f57] transition-colors whitespace-nowrap">
                Call (317) 648-2983
              </a>
              <button
                onClick={() => setShowBooking(false)}
                className="px-6 py-3 text-[#4A5568] font-semibold hover:text-[#1A3A52] transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
